import { useState, useEffect, useRef } from 'react';
import type { Deal, DealStage, BuyerProspect, ActivityEvent, Comp } from '../../types';
import { useUIStore } from '../../store/ui';
import { useDealsStore } from '../../store/deals';
import { dealsApi, airtableApi } from '../../services/api';
import { uid, nowIso } from '../../lib/utils';

const STAGES: DealStage[] = ['intake', 'title', 'package', 'pitching', 'closed'];
const STAGE_LABEL: Record<DealStage, string> = {
  intake: 'Intake', title: 'Title', package: 'Package', pitching: 'Pitch', closed: 'Close',
};

interface Props { deal: Deal; }

type ModalState =
  | { kind: 'none' }
  | { kind: 'close-won' }
  | { kind: 'close-lost' }
  | { kind: 'delete' }
  | { kind: 'reopen' }
  | { kind: 'airtable-push' };

export function IdentityCard({ deal }: Props) {
  const saveDeal = useDealsStore(s => s.saveDeal);

  const stageIdx = STAGES.indexOf(deal.stage);
  const isClosed = deal.stage === 'closed' && !!deal.closedOutcome;

  const parts = deal.ownerName.split(' ');
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(' ');

  const setStage = (s: DealStage) => {
    void saveDeal({ ...deal, stage: s, updatedAt: nowIso() });
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [menuOpen]);

  return (
    <>
      <div className="identity">
        <div className="identity-meta">
          <span>Deal · {deal.ref}</span>
          <span className="identity-id">{deal.county}</span>
          <div className="deal-actions-menu" ref={menuRef}>
            <button className="deal-actions-trigger" onClick={() => setMenuOpen(v => !v)} title="Deal actions">⋯</button>
            {menuOpen && (
              <div className="deal-actions-pop">
                <button onClick={() => { setMenuOpen(false); setModal({ kind: 'airtable-push' }); }}>
                  Push to Airtable
                </button>
                <div className="deal-actions-divider" />
                {!isClosed && (
                  <>
                    <button onClick={() => { setMenuOpen(false); setModal({ kind: 'close-won' }); }}>
                      Mark closed-won
                    </button>
                    <button onClick={() => { setMenuOpen(false); setModal({ kind: 'close-lost' }); }}>
                      Mark closed-lost
                    </button>
                    <div className="deal-actions-divider" />
                  </>
                )}
                {isClosed && (
                  <>
                    <button onClick={() => { setMenuOpen(false); setModal({ kind: 'reopen' }); }}>
                      Re-open deal
                    </button>
                    <div className="deal-actions-divider" />
                  </>
                )}
                <button className="danger" onClick={() => { setMenuOpen(false); setModal({ kind: 'delete' }); }}>
                  Delete permanently…
                </button>
              </div>
            )}
          </div>
        </div>

        {isClosed && <ClosedBanner deal={deal} />}

        <h1 className="owner-name">
          {first && <>{first} </>}
          <em>{last}</em>
        </h1>
        <p className="owner-context">
          <strong>{deal.trs}</strong>
          {deal.ownerContext && <> · {deal.ownerContext}</>}
        </p>

        <div className="stage-track">
          {STAGES.map((s, i) => (
            <div
              key={s}
              className={`stage-step ${i < stageIdx ? 'done' : i === stageIdx ? 'current' : ''}`}
              title={STAGE_LABEL[s]}
            />
          ))}
        </div>
        <div className="stage-labels">
          {STAGES.map((s, i) => (
            <button
              key={s}
              className={i < stageIdx ? 'done' : i === stageIdx ? 'current' : ''}
              onClick={() => setStage(s)}
            >
              {STAGE_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="identity-grid">
          <div className="identity-field">
            <span className="identity-field-label">Tract</span>
            <span className="identity-field-value">{deal.trs}</span>
          </div>
          <div className="identity-field">
            <span className="identity-field-label">NMA est.</span>
            <span className="identity-field-value">
              {deal.nmaEstimate ?? '—'}{' '}
              {deal.nmaEstimate && (
                <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--mono)', fontSize: 11 }}>net</span>
              )}
            </span>
          </div>
          <div className="identity-field">
            <span className="identity-field-label">Operator</span>
            <span className="identity-field-value">{deal.operator}</span>
          </div>
          <div className="identity-field">
            <span className="identity-field-label">Status</span>
            <span className="identity-field-value">{deal.status}</span>
          </div>
          <div className="identity-field">
            <span className="identity-field-label">Source</span>
            <span className="identity-field-value muted">{deal.source}</span>
          </div>
          <div className="identity-field">
            <span className="identity-field-label">Close ID</span>
            <span className="identity-field-value muted">{deal.closeId ?? '—'}</span>
          </div>
        </div>
      </div>

      {modal.kind === 'close-won'      && <CloseWonModal     deal={deal} onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'close-lost'     && <CloseLostModal    deal={deal} onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'reopen'         && <ReopenModal       deal={deal} onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'delete'         && <DeleteModal       deal={deal} onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'airtable-push'  && <AirtablePushModal deal={deal} onClose={() => setModal({ kind: 'none' })} />}
    </>
  );
}

function ClosedBanner({ deal }: { deal: Deal }) {
  if (deal.closedOutcome === 'won') {
    const dateStr = deal.closedAt ? new Date(deal.closedAt).toLocaleDateString() : '';
    const priceStr = deal.salePriceTotal != null
      ? `$${deal.salePriceTotal.toLocaleString()}`
      : (deal.salePricePerNma != null ? `$${deal.salePricePerNma.toLocaleString()}/NMA` : '');
    return (
      <div className="closed-banner won">
        <span className="closed-banner-tag">CLOSED · WON</span>
        <span className="closed-banner-detail">
          {deal.soldTo && <>Sold to <strong>{deal.soldTo}</strong></>}
          {priceStr && <> · {priceStr}</>}
          {dateStr && <> · {dateStr}</>}
        </span>
      </div>
    );
  }
  if (deal.closedOutcome === 'lost') {
    const dateStr = deal.closedAt ? new Date(deal.closedAt).toLocaleDateString() : '';
    return (
      <div className="closed-banner lost">
        <span className="closed-banner-tag">CLOSED · LOST</span>
        <span className="closed-banner-detail">
          {deal.lostReason && <>{deal.lostReason}</>}
          {dateStr && <> · {dateStr}</>}
        </span>
      </div>
    );
  }
  return null;
}

// ─── CLOSE-WON ─────────────────────────────────────────────
function CloseWonModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const saveDeal = useDealsStore(s => s.saveDeal);
  const saveComp = useDealsStore(s => s.saveComp);
  const appendActivity = useDealsStore(s => s.appendActivity);
  const buyers = useDealsStore(s => s.buyers);

  const [soldTo, setSoldTo] = useState('');
  const [pricePerNma, setPricePerNma] = useState('');
  const [priceTotal, setPriceTotal] = useState('');
  const [closedAt, setClosedAt] = useState(new Date().toISOString().slice(0, 10));
  const [createComp, setCreateComp] = useState(true);
  const [busy, setBusy] = useState(false);

  // Auto-derive total from per-NMA when both known
  useEffect(() => {
    if (pricePerNma && deal.nmaEstimate && !priceTotal) {
      const total = parseFloat(pricePerNma) * deal.nmaEstimate;
      if (!isNaN(total)) setPriceTotal(Math.round(total).toString());
    }
  }, [pricePerNma]); // eslint-disable-line

  const onConfirm = async () => {
    if (!soldTo.trim()) {
      alert('Please specify the buyer.');
      return;
    }
    setBusy(true);
    try {
      const closedIso = new Date(closedAt).toISOString();
      const updated: Deal = {
        ...deal,
        stage: 'closed',
        status: 'Closed · sold',
        closedOutcome: 'won',
        closedAt: closedIso,
        soldTo: soldTo.trim(),
        salePricePerNma: pricePerNma ? parseFloat(pricePerNma) : undefined,
        salePriceTotal:  priceTotal   ? parseFloat(priceTotal)   : undefined,
        updatedAt: nowIso(),
      };
      await saveDeal(updated);

      if (createComp && deal.nmaEstimate && (pricePerNma || priceTotal)) {
        const ppNma = pricePerNma
          ? parseFloat(pricePerNma)
          : (priceTotal ? parseFloat(priceTotal) / deal.nmaEstimate : 0);
        const tot = priceTotal
          ? parseFloat(priceTotal)
          : (pricePerNma ? parseFloat(pricePerNma) * deal.nmaEstimate : 0);
        const comp: Comp = {
          id: uid('cm'),
          dealId: deal.id,
          trs: deal.trs,
          date: closedAt,
          buyer: soldTo.trim().toUpperCase(),
          seller: deal.ownerName,
          netAcres: deal.nmaEstimate,
          pricePerNma: ppNma,
          totalPrice: tot,
          source: 'Timberline · own deal',
          notes: `Sold via Timberline · ${deal.ref}`,
        };
        await saveComp(comp);
      }

      const ae: ActivityEvent = {
        id: uid('ae'), dealId: deal.id, kind: 'package-version',
        text: `Deal closed-won · sold to ${soldTo.trim()}${pricePerNma ? ` at $${pricePerNma}/NMA` : ''}`,
        occurredAt: nowIso(), byClaude: false,
      };
      await appendActivity(ae);

      onClose();
    } finally {
      setBusy(false);
    }
  };

  // Simple buyer-name autocomplete
  const matchingBuyers = soldTo.length > 0
    ? buyers
        .filter(b => b.canonicalName.toLowerCase().includes(soldTo.toLowerCase()))
        .slice(0, 5)
    : [];

  return (
    <ModalShell title="Mark closed-won" onClose={onClose}>
      <p className="modal-p">
        This archives <strong>{deal.ownerName}</strong> ({deal.trs}) as a sold deal.
        The data stays — you'll find it under "Show closed" in the deal index.
      </p>
      <div className="modal-form">
        <ModalField label="Sold to">
          <input
            className="modal-input"
            type="text"
            placeholder="Buyer name…"
            value={soldTo}
            onChange={e => setSoldTo(e.target.value)}
            list="closewon-buyers"
            autoFocus
          />
          {matchingBuyers.length > 0 && soldTo !== matchingBuyers[0]?.canonicalName && (
            <datalist id="closewon-buyers">
              {matchingBuyers.map((b: BuyerProspect) => (
                <option key={b.id} value={b.canonicalName} />
              ))}
            </datalist>
          )}
        </ModalField>
        <div className="modal-row-2">
          <ModalField label="Price / NMA ($)">
            <input className="modal-input" type="number" inputMode="decimal" placeholder="58000"
              value={pricePerNma} onChange={e => setPricePerNma(e.target.value)} />
          </ModalField>
          <ModalField label="Total price ($)">
            <input className="modal-input" type="number" inputMode="decimal" placeholder="746750"
              value={priceTotal} onChange={e => setPriceTotal(e.target.value)} />
          </ModalField>
        </div>
        <ModalField label="Closed on">
          <input className="modal-input" type="date" value={closedAt}
            onChange={e => setClosedAt(e.target.value)} />
        </ModalField>
        <label className="modal-checkbox">
          <input type="checkbox" checked={createComp} onChange={e => setCreateComp(e.target.checked)} />
          Add to comps database (recommended — your own deals are the best comps)
        </label>
      </div>
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={onConfirm} disabled={busy}>
          {busy ? 'Saving…' : 'Mark closed-won'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── CLOSE-LOST ────────────────────────────────────────────
function CloseLostModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const saveDeal = useDealsStore(s => s.saveDeal);
  const appendActivity = useDealsStore(s => s.appendActivity);

  const [reason, setReason] = useState('Declined');
  const [otherReason, setOtherReason] = useState('');
  const [closedAt, setClosedAt] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const REASONS = [
    'Declined',
    'No response',
    'Lost to competitor',
    'Owner changed mind',
    'Not the right owner',
    'Other',
  ];

  const onConfirm = async () => {
    setBusy(true);
    try {
      const finalReason = reason === 'Other' ? (otherReason.trim() || 'Other') : reason;
      const closedIso = new Date(closedAt).toISOString();
      const updated: Deal = {
        ...deal,
        stage: 'closed',
        status: `Closed · ${finalReason.toLowerCase()}`,
        closedOutcome: 'lost',
        closedAt: closedIso,
        lostReason: finalReason,
        updatedAt: nowIso(),
      };
      await saveDeal(updated);

      const ae: ActivityEvent = {
        id: uid('ae'), dealId: deal.id, kind: 'package-version',
        text: `Deal closed-lost · ${finalReason}`,
        occurredAt: nowIso(), byClaude: false,
      };
      await appendActivity(ae);

      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Mark closed-lost" onClose={onClose}>
      <p className="modal-p">
        This archives <strong>{deal.ownerName}</strong> ({deal.trs}) as a lost deal.
        The data stays — useful when you re-engage this owner later.
      </p>
      <div className="modal-form">
        <ModalField label="Reason">
          <select className="modal-input" value={reason} onChange={e => setReason(e.target.value)}>
            {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </ModalField>
        {reason === 'Other' && (
          <ModalField label="Specify">
            <input className="modal-input" type="text" placeholder="Free text…"
              value={otherReason} onChange={e => setOtherReason(e.target.value)} autoFocus />
          </ModalField>
        )}
        <ModalField label="Closed on">
          <input className="modal-input" type="date" value={closedAt}
            onChange={e => setClosedAt(e.target.value)} />
        </ModalField>
      </div>
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={onConfirm} disabled={busy}>
          {busy ? 'Saving…' : 'Mark closed-lost'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── REOPEN ────────────────────────────────────────────────
function ReopenModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const saveDeal = useDealsStore(s => s.saveDeal);
  const appendActivity = useDealsStore(s => s.appendActivity);
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    setBusy(true);
    try {
      const updated: Deal = {
        ...deal,
        stage: 'pitching',
        status: 'Re-opened',
        closedOutcome: undefined,
        closedAt: undefined,
        soldTo: undefined,
        salePricePerNma: undefined,
        salePriceTotal: undefined,
        lostReason: undefined,
        updatedAt: nowIso(),
      };
      await saveDeal(updated);
      const ae: ActivityEvent = {
        id: uid('ae'), dealId: deal.id, kind: 'package-version',
        text: 'Deal re-opened',
        occurredAt: nowIso(), byClaude: false,
      };
      await appendActivity(ae);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Re-open deal" onClose={onClose}>
      <p className="modal-p">
        Re-open <strong>{deal.ownerName}</strong>? This clears the closed-{deal.closedOutcome} status
        and moves it back to the pitching stage. The closed-out details will be cleared.
      </p>
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={onConfirm} disabled={busy}>
          {busy ? 'Re-opening…' : 'Re-open'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── DELETE ────────────────────────────────────────────────
function DeleteModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const closeDeal = useUIStore(s => s.closeDeal);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const required = deal.ownerName;

  const onConfirm = async () => {
    if (confirmText.trim() !== required) {
      setError(`Please type "${required}" exactly to confirm.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await dealsApi.remove(deal.id);
      // Remove from local store + close the tab
      useDealsStore.setState(s => ({
        deals: s.deals.filter(d => d.id !== deal.id),
        byDealId: Object.fromEntries(Object.entries(s.byDealId).filter(([k]) => k !== deal.id)),
      }));
      closeDeal(deal.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Delete deal permanently" onClose={onClose}>
      <p className="modal-p" style={{ color: '#d05050' }}>
        <strong>This cannot be undone.</strong> All files in R2, the chain, math, narrative,
        flags, comps tied to this deal, conversation, and activity will be deleted.
      </p>
      <p className="modal-p">
        For sold or lost deals, use <strong>Mark closed-won</strong> or <strong>Mark closed-lost</strong> instead — that keeps the data for your records.
      </p>
      <p className="modal-p">
        To confirm, type the owner name <strong>{required}</strong> below:
      </p>
      <div className="modal-form">
        <ModalField label="Confirm">
          <input className="modal-input" type="text" value={confirmText}
            onChange={e => setConfirmText(e.target.value)} autoFocus />
        </ModalField>
      </div>
      {error && <div style={{ color: '#d05050', fontSize: 11.5, marginBottom: 8 }}>{error}</div>}
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-primary danger" onClick={onConfirm} disabled={busy || confirmText.trim() !== required}>
          {busy ? 'Deleting…' : 'Delete forever'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── AIRTABLE PUSH ─────────────────────────────────────────
function AirtablePushModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await airtableApi.push(deal.id);
      setSummary(r.summary);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Push to Airtable" onClose={onClose}>
      {!summary && !error && (
        <>
          <p className="modal-p">
            Pushes this deal's <strong>owner record</strong>, <strong>leases</strong> from the chain, and any <strong>comps</strong> to your Airtable base. Idempotent — running it twice updates the existing records instead of creating duplicates.
          </p>
          <p className="modal-p" style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>
            Records are matched by Workbench Deal/Link/Comp ID. Make sure your Airtable tables have those columns (or they'll be created on first push).
          </p>
          <div className="modal-actions">
            <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-primary" onClick={onConfirm} disabled={busy}>
              {busy ? 'Pushing…' : 'Push now'}
            </button>
          </div>
        </>
      )}
      {summary && (
        <>
          <p className="modal-p">
            <strong>Pushed.</strong>
          </p>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, lineHeight: 1.7, color: 'var(--text-dim)' }}>
            <div>Owners: {summary.ownersCreated} created · {summary.ownersUpdated} updated</div>
            <div>Leases: {summary.leasesCreated} created · {summary.leasesUpdated} updated</div>
            <div>Comps:  {summary.compsCreated} created · {summary.compsUpdated} updated</div>
          </div>
          {summary.warnings.length > 0 && (
            <div className="modal-p" style={{ marginTop: 12, color: 'var(--amber)', fontSize: 11.5 }}>
              ⚠ {summary.warnings.length} warning{summary.warnings.length > 1 ? 's' : ''}:
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {summary.warnings.slice(0, 5).map((w: string, i: number) => (
                  <li key={i} style={{ fontSize: 11 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="modal-actions">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      )}
      {error && (
        <>
          <div style={{ color: '#d05050', fontSize: 12, marginTop: 8, marginBottom: 12 }}>
            {error}
          </div>
          <div className="modal-actions">
            <button className="btn-ghost" onClick={onClose}>Close</button>
            <button className="btn-primary" onClick={() => { setError(null); setBusy(false); }}>
              Retry
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ─── Modal primitives ──────────────────────────────────────
function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="modal-field">
      <span className="modal-field-label">{label}</span>
      {children}
    </label>
  );
}
